import React from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Text, View } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { usePersistentCart } from '../context/PersistentCartContext';
import { useCartAuth } from '../hooks/useCartAuth';

// Screens
import LoginScreen from '../screens/LoginScreen';
import RegisterScreen from '../screens/RegisterScreen';
import DashboardScreenSimple from '../screens/DashboardScreenSimple';
import SearchScreen from '../screens/SearchScreen';
import CartScreenSimple from '../screens/CartScreenSimple';
import CartCheckoutScreen from '../screens/CartCheckoutScreen';
import { ProductListScreen } from '../screens/ProductListScreen';
import ProductDetailScreenNew from '../screens/ProductDetailScreenNew';
import { ProfileScreen } from '../screens/ProfileScreen';
import { ReferidosScreen } from '../screens/ReferidosScreen';
import FavoritesScreen from '../screens/FavoritesScreen';
import AuthLoadingScreen from '../screens/AuthLoadingScreen';
import DebugScreen from '../screens/DebugScreen';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

// Stack de autenticación (sin autenticar)
const AuthStack = () => (
  <Stack.Navigator 
    screenOptions={{ 
      headerShown: false,
      cardStyle: { backgroundColor: '#f8f9fa' }
    }}
  >
    <Stack.Screen name="Login" component={LoginScreen} />
    <Stack.Screen name="Register" component={RegisterScreen} />
    <Stack.Screen name="Debug" component={DebugScreen} />
  </Stack.Navigator>
);

// Tabs principales (autenticado)
const MainTabs = () => {
  const { state } = usePersistentCart();
  const cartCount = state.itemsCount;

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused }) => {
          let icon = '❓';
          if (route.name === 'Dashboard') {
            icon = focused ? '🏠' : '🏡';
          } else if (route.name === 'Search') {
            icon = focused ? '🔍' : '🔎';
          } else if (route.name === 'Cart') {
            icon = focused ? '🛒' : '🛍️';
          } else if (route.name === 'Profile') {
            icon = focused ? '👤' : '👤';
          }

          return (
            <View style={{ position: 'relative', alignItems: 'center' }}>
              <Text style={{ fontSize: 24 }}>{icon}</Text>
              {route.name === 'Cart' && cartCount > 0 && (
                <View style={{
                  position: 'absolute',
                  top: -2,
                  right: -6,
                  backgroundColor: '#e74c3c',
                  borderRadius: 10,
                  minWidth: 20,
                  height: 20,
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Text style={{
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </Text>
                </View>
              )}
            </View>
          );
        },
        tabBarActiveTintColor: '#e74c3c',
        tabBarInactiveTintColor: '#95a5a6',
        tabBarStyle: {
          backgroundColor: 'white',
          borderTopWidth: 1,
          borderTopColor: '#e1e8ed',
          paddingBottom: 5,
          paddingTop: 5,
          height: 60,
        },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '600',
        },
        headerShown: false,
      })}
    >
      <Tab.Screen 
        name="Dashboard" 
        component={DashboardScreenSimple}
        options={{
          title: 'Inicio',
          tabBarLabel: 'Inicio',
        }}
      />
      <Tab.Screen 
        name="Search" 
        component={SearchScreen}
        options={{
          title: 'Buscar',
          tabBarLabel: 'Buscar',
        }}
      />
      <Tab.Screen 
        name="Cart" 
        component={CartScreenSimple}
        options={{
          title: 'Carrito',
          tabBarLabel: 'Carrito',
        }}
      />
      <Tab.Screen 
        name="Profile" 
        component={ProfileScreen}
        options={{
          title: 'Perfil',
          tabBarLabel: 'Perfil',
        }}
      />
    </Tab.Navigator>
  );
};

// Stack principal con tabs + detalles
const MainStack = () => (
  <Stack.Navigator
    screenOptions={{
      headerStyle: {
        backgroundColor: '#e74c3c',
        elevation: 0,
        shadowOpacity: 0,
      },
      headerTitleStyle: {
        color: 'white',
        fontSize: 18,
        fontWeight: 'bold',
      },
      headerTintColor: 'white',
      cardStyle: { backgroundColor: '#f8f9fa' },
    }}
  >
    <Stack.Screen 
      name="MainTabs" 
      component={MainTabs}
      options={{ headerShown: false }}
    />
    <Stack.Screen 
      name="ProductDetail" 
      component={ProductDetailScreenNew as any}
      options={{ 
        title: 'Detalle del Producto',
        headerShown: false 
      }}
    />
    <Stack.Screen 
      name="ProductList" 
      component={ProductListScreen}
      options={{ title: 'Productos' }}
    />
    <Stack.Screen 
      name="Favorites" 
      component={FavoritesScreen}
      options={{ 
        title: 'Mis Favoritos',
        headerShown: true,
        headerStyle: {
          backgroundColor: '#e74c3c',
          elevation: 0,
          shadowOpacity: 0,
        },
        headerTitleStyle: {
          color: 'white',
          fontSize: 18,
          fontWeight: 'bold',
        },
        headerTintColor: 'white',
      }}
    />
    <Stack.Screen 
      name="Referidos" 
      component={ReferidosScreen}
      options={{ 
        title: 'Mis Referidos',
        headerShown: false
      }}
    />
    <Stack.Screen 
      name="CartCheckout" 
      component={CartCheckoutScreen}
      options={{ 
        title: 'Finalizar Compra',
        headerShown: true 
      }}
    />
  </Stack.Navigator>
);

// Navegador principal
function AppNavigator(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Integrar sincronización automática del carrito
  useCartAuth();

  console.log('🧭 AppNavigator: isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  return (
    <NavigationContainer>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {isLoading ? (
          <Stack.Screen name="AuthLoading" component={AuthLoadingScreen} />
        ) : isAuthenticated ? (
          <Stack.Screen name="Main" component={MainStack} />
        ) : (
          <Stack.Screen name="Auth" component={AuthStack} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
};

export default AppNavigator;
